/**
 * ArabicTextPanel Component
 *
 * Displays the original Arabic paragraph text as a selectable panel
 * positioned on the LEFT side of the PDF viewer.
 *
 * Design principles (Orbit / Andy Matuschak aesthetic):
 * - Clean, minimal layout with generous whitespace
 * - Warm pastel backgrounds matching the translation color scheme
 * - Thoughtful typography using Scheherazade New for Arabic
 * - Smooth, subtle hover interactions
 * - Text is selectable (unlike PDF which may be scanned/image-based)
 *
 * The panel uses the same colorIndex as the corresponding:
 * - ParagraphHighlight (on the PDF)
 * - TranslationBlob (English text on the right)
 * This creates a visual trio that links original → highlight → translation.
 */

import React from "react";
import { PdfParagraph, TranslatedParagraph } from "./translationTypes.js";
import { getColorForIndex } from "./colorPalette.js";

interface ArabicTextPanelProps {
  /** The paragraph data (includes original text and bbox) */
  paragraph: PdfParagraph;
  /** The translation data (includes originalText and colorIndex) */
  translation: TranslatedParagraph;
  /** Viewport-relative Y position (pixels) */
  top: number;
  /** Viewport-relative X position (pixels) */
  left: number;
  /** Panel width (pixels) */
  width: number;
  /** Whether this panel is currently hovered */
  isHovered: boolean;
  /** Callback when hover state changes */
  onHover: (hovering: boolean) => void;
}

export function ArabicTextPanel({
  paragraph,
  translation,
  top,
  left,
  width,
  isHovered,
  onHover,
}: ArabicTextPanelProps) {
  const colors = getColorForIndex(translation.colorIndex);

  // Use translation.originalText (from Gemini) or fall back to paragraph.text
  const arabicText = translation.originalText || paragraph.text;

  return (
    <div
      className="translationArabicBlob"
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      style={{
        position: "absolute",
        top,
        left,
        width,
        maxWidth: 300,
        padding: "12px 16px",
        backgroundColor: colors.bg,
        // Border on the right side (facing the PDF) - mirrors TranslationBlob's left border
        borderRight: `4px solid ${colors.border}`,
        borderRadius: "8px 0 0 8px",
        boxShadow: isHovered
          ? "0 4px 16px rgba(0, 0, 0, 0.12)"
          : "0 2px 8px rgba(0, 0, 0, 0.06)",
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        // Subtle leftward shift on hover (opposite of right blob's rightward shift)
        transform: isHovered ? "translateX(-4px)" : "none",
        cursor: "default",
        zIndex: isHovered ? 101 : 100,
        // RTL text direction for Arabic
        direction: "rtl",
        textAlign: "right",
        // Critical: allow text selection for copying
        userSelect: "text",
      }}
    >
      <p
        style={{
          margin: 0,
          // Scheherazade New - elegant Arabic typeface loaded in index.html
          fontFamily: '"Scheherazade New", serif',
          // Larger size for Arabic readability
          fontSize: 20,
          // Generous line height for Arabic diacritics and clarity
          lineHeight: 1.9,
          color: "rgba(0, 0, 0, 0.9)",
          // Text rendering for crisp Arabic glyphs
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
        }}
      >
        {arabicText}
      </p>
    </div>
  );
}

export default ArabicTextPanel;
